import { useFeatures } from '@shared/feature-flags';
import { useHeaderDetails } from '@shared/header';
import { HeaderDetails } from '@shared/header/header-details.context';
import { transitionAll } from '@shared/styled/animated';
import { Page } from 'grommet';
import { every, isEmpty, negate, some, values } from 'lodash';
import React, { useState } from 'react';
import {
  LiaAngleUpSolid,
  LiaBell,
  LiaBookMedicalSolid,
  LiaBrainSolid,
  LiaCalendar,
  LiaCalendarPlus,
  LiaChartPieSolid,
  LiaCloudDownloadAltSolid,
  LiaDatabaseSolid,
  LiaEdit,
  LiaFileMedicalAltSolid,
  LiaFileMedicalSolid,
  LiaHomeSolid,
  LiaNewspaper,
  LiaPillsSolid, LiaQuestionCircle, LiaQuestionCircleSolid,
  LiaShoppingBasketSolid,
  LiaSignOutAltSolid,
  LiaStreamSolid,
  LiaTagSolid, LiaUser, LiaUserCogSolid,
  LiaUserTieSolid
} from 'react-icons/lia';

import { Avatar } from '@/components-new/avatar';
import { Breadcrumbs } from '@/components-new/breadcrumbs';
import { Dropdown, DropdownButton, DropdownItem, DropdownLabel, DropdownMenu } from '@/components-new/dropdown';
import { Link } from '@/components-new/link';
import { Navbar, NavbarItem, NavbarSection, NavbarSpacer } from '@/components-new/navbar';
import { PageTitleRow } from '@/components-new/page-title-row';
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeading,
  SidebarItem,
  SidebarLabel,
  SidebarSection, SidebarSpacer
} from '@/components-new/sidebar';
import { SidebarLayout } from '@/components-new/sidebar-layout';
import { AskArtiaPanel } from '@/features/ai/components/ask-artia-panel';
import { NewClientOnboardingDialog } from '@/features/onboarding/components/new-client-onboarding-dialog';
import { useAuthFlow } from '@/hooks/use-auth-flow';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useIsCurrentRoute } from '@/hooks/use-is-current-route';
import { CurrentUser } from '@/types/me';
import { PropsWithRenderableChildren } from '@/types/react';

const AnimatedPage = transitionAll(Page);

const AuthorizedSidebarSection = (
  { sectionHeader, permissions, children }: PropsWithRenderableChildren<{ sectionHeader: React.ReactNode, permissions: Record<string, boolean> }, { permissions: Record<string, boolean> }>
) => {
  const sectionUnauthorized = !some(permissions);

  if (sectionUnauthorized) return null;

  return (
    <SidebarSection>
      {sectionHeader}
      {typeof children === 'function' ? children({ permissions }) : children}
    </SidebarSection>
  );
};

const DashboardLayout = ({ children, user }: { children: React.ReactNode, user: CurrentUser }) => {
  const { signOutAsync } = useAuthFlow();
  const { user: currentUser, hasPolicies } = useCurrentUser();
  const isCurrentRoute = useIsCurrentRoute();

  const { headerDetails } = useHeaderDetails();
  const isHeaderDetailsEmpty = (headerDetails: HeaderDetails) => every(values(headerDetails), isEmpty);
  const hasHeaderDetails = negate(isHeaderDetailsEmpty);

  const isActiveUser = user.client == undefined || user.client?.isActive;

  const isClientUser = hasPolicies(['isClientUser']);
  const isCalendarEnabled = currentUser.premiumFeatures.includes('calendar') && isActiveUser;
  const isDrugInfoEnabled = currentUser.premiumFeatures.includes('drug_info') && isActiveUser;
  const isNotificationsEnabled = currentUser.premiumFeatures.includes('notifications') && isActiveUser;
  const hasStateReports = (currentUser.premiumFeatures.includes('state_reports') || currentUser.premiumFeatures.includes('state_reports_premium')) && isActiveUser;
  const hasMarketReports = (currentUser.premiumFeatures.includes('market_reports') || !currentUser.client?.id) && isActiveUser;
  const { getFeature } = useFeatures();
  const isStateReportsFeatureEnabled = getFeature('clientStateReports');
  const isStateReportsEnabled = hasStateReports && isStateReportsFeatureEnabled;
  const isUserSettingsAvailable = isClientUser;
  const isAiEnabled = currentUser.premiumFeatures.includes('ai_insights') && isActiveUser;
  const [showAskArtia, setShowAskArtia] = useState(false);

  const currentYear = new Date().getFullYear();

  const handleSignout = async () => {
    void signOutAsync();
  };

  return (
    <SidebarLayout
      sidebar={
        <Sidebar className="dark">
          <SidebarBody>
            <Link href="/" aria-label="Home" className="mb-4 mt-2 flex h-10 shrink-0 items-center px-2">
              <img
                className="h-7 w-auto"
                src="/artia-solutions.svg"
                alt="Artia Solutions Logo"
              />
            </Link>
            <SidebarSection>
              <SidebarItem href="/" current={isCurrentRoute('/')}>
                <LiaHomeSolid size="1.25rem"/>
                <SidebarLabel>Home</SidebarLabel>
              </SidebarItem>
              {isNotificationsEnabled
                && <SidebarItem href="/daily-dose" current={isCurrentRoute('/daily-dose')}>
                  <LiaNewspaper size="1.25rem"/>
                  <SidebarLabel>Daily Dose</SidebarLabel>
                </SidebarItem>
              }
              {isDrugInfoEnabled
                && <SidebarItem href="/drugs" current={isCurrentRoute('/drugs')}>
                  <LiaPillsSolid size="1.25rem"/>
                  <SidebarLabel>Drug Coverage</SidebarLabel>
                </SidebarItem>
              }
              {isCalendarEnabled
                && <SidebarItem href="/calendar/tracked" current={isCurrentRoute('/calendar')}>
                  <LiaCalendar size="1.25rem"/>
                  <SidebarLabel>Calendar</SidebarLabel>
                </SidebarItem>
              }
              {isNotificationsEnabled
                && <SidebarItem href="/notifications" current={isCurrentRoute('/notifications')}>
                  <LiaBell size="1.25rem"/>
                  <SidebarLabel>Notifications</SidebarLabel>
                </SidebarItem>
              }
              {isStateReportsEnabled && (
                <SidebarItem href="/state-reports" current={isCurrentRoute('/state-reports')}>
                  <LiaFileMedicalSolid size="1.25rem"/>
                  <SidebarLabel>State Reports</SidebarLabel>
                </SidebarItem>
              )}
              {isAiEnabled && (
                <SidebarItem onClick={() => setShowAskArtia(true)}>
                  <LiaBrainSolid size="1.25rem"/>
                  <SidebarLabel>Ask Artia</SidebarLabel>
                </SidebarItem>
              )}
            </SidebarSection>

            <AuthorizedSidebarSection
              sectionHeader={<SidebarHeading>Drug Information</SidebarHeading>}
              permissions={{
                canAccessCoverage: hasPolicies(['isAnyArtiaUser']),
                canAccessDrugs: (hasPolicies(['isAnyArtiaUser'])),
                canAccessChangelog: hasPolicies(['isAnyArtiaUser']),
              }}
            >
              {({ permissions }) => (
                <>
                  {permissions.canAccessDrugs && (
                    <SidebarItem href="/drugs" current={isCurrentRoute('/drugs')}>
                      <LiaPillsSolid size="1.25rem"/>
                      <SidebarLabel>Drugs</SidebarLabel>
                    </SidebarItem>
                  )}
                  {permissions.canAccessCoverage && (
                    <SidebarItem href="/drug-coverage" current={isCurrentRoute('/drug-coverage')}>
                      <LiaBookMedicalSolid size="1.25rem"/>
                      <SidebarLabel>Coverage</SidebarLabel>
                    </SidebarItem>
                  )}
                  {permissions.canAccessChangelog && (
                    <SidebarItem href="/changelog" current={isCurrentRoute('/changelog')}>
                      <LiaStreamSolid size="1.25rem"/>
                      <SidebarLabel>Changelog</SidebarLabel>
                    </SidebarItem>
                  )}
                </>
              )}
            </AuthorizedSidebarSection>
            <AuthorizedSidebarSection
              sectionHeader={<SidebarHeading>Analysis</SidebarHeading>}
              permissions={{
                canAccessBidAnalyses: hasPolicies(['isAssignedToAnyClient', 'canManageBidAnalyses']),
                canAccessMarketBaskets: hasPolicies(['isAssignedToAnyClient', 'canManageMarketBaskets']),
                canAccessStateReports: hasPolicies(['isAnyArtiaUser']),
              }}
            >
              {({ permissions }) => (
                <>
                  {permissions.canAccessBidAnalyses && (
                    <SidebarItem href="/bid-analyses" current={isCurrentRoute('/bid-analyses')}>
                      <LiaChartPieSolid size="1.25rem"/>
                      <SidebarLabel>Bid Analyses</SidebarLabel>
                    </SidebarItem>
                  )}
                  {permissions.canAccessMarketBaskets && (
                    <SidebarItem href="/market-baskets" current={isCurrentRoute('/market-baskets')}>
                      <LiaShoppingBasketSolid size="1.25rem"/>
                      <SidebarLabel>Market Baskets</SidebarLabel>
                    </SidebarItem>
                  )}
                  {permissions.canAccessStateReports && (
                    <SidebarItem href="/state-reports" current={isCurrentRoute('/state-reports')}>
                      <LiaFileMedicalSolid size="1.25rem"/>
                      <SidebarLabel>State Reports</SidebarLabel>
                    </SidebarItem>
                  )}
                </>
              )}
            </AuthorizedSidebarSection>
            <AuthorizedSidebarSection
              sectionHeader={<SidebarHeading>Management</SidebarHeading>}
              permissions={{
                canAccessClassifications: hasPolicies(['canManageClassifications']),
                canAccessClients: hasPolicies(['isAnyArtiaUser']),
                canAccessBoardMeetings: hasPolicies(['isAnyArtiaUser']),
                canManageUsers: hasPolicies(['canManageUsers']),
                canManagePosts: hasPolicies(['isAnyArtiaUser']) // TODO: We don't have a specific role for this yet, but Artia will probably want to lock this down to certain users soon.
              }}
            >
              {({ permissions }) => (
                <>
                  {permissions.canAccessBoardMeetings && (
                    <SidebarItem href="/board-meetings" current={isCurrentRoute('/board-meetings')}>
                      <LiaCalendarPlus size="1.25rem"/>
                      <SidebarLabel>Board Meetings</SidebarLabel>
                    </SidebarItem>
                  )}
                  {permissions.canAccessClassifications && (
                    <SidebarItem href="/classifications" current={isCurrentRoute('/classifications')}>
                      <LiaTagSolid size="1.25rem"/>
                      <SidebarLabel>Classifications</SidebarLabel>
                    </SidebarItem>
                  )}
                  {permissions.canAccessClients && (
                    <SidebarItem href="/clients" current={isCurrentRoute('/clients')}>
                      <LiaUserTieSolid size="1.25rem"/>
                      <SidebarLabel>Clients</SidebarLabel>
                    </SidebarItem>
                  )}
                  {permissions.canManagePosts && (
                    <SidebarItem href="/posts" current={isCurrentRoute('/posts')}>
                      <LiaEdit size="1.25rem"/>
                      <SidebarLabel>Posts</SidebarLabel>
                    </SidebarItem>
                  )}
                  {permissions.canManageUsers && (
                    <SidebarItem href="/admin/users" current={isCurrentRoute('/admin/users')}>
                      <LiaUser size="1.25rem"/>
                      <SidebarLabel>Users</SidebarLabel>
                    </SidebarItem>
                  )}
                </>
              )}
            </AuthorizedSidebarSection>

            {/* Market Reports Section */}
            {hasMarketReports && (
              <SidebarSection>
                <SidebarHeading>Market Reports</SidebarHeading>
                <SidebarItem href="/market-reports/therapeutic-class-report" current={isCurrentRoute('/market-reports/therapeutic-class-report')}>
                  <LiaFileMedicalAltSolid size="1.25rem"/>
                  <SidebarLabel>Therapeutic Class Report</SidebarLabel>
                </SidebarItem>

                  <SidebarItem href="/market-reports/market-share-report" current={isCurrentRoute('/market-reports/market-share-report')}>
                    <LiaChartPieSolid size="1.25rem"/>
                    <SidebarLabel>Market Share Report</SidebarLabel>
                  </SidebarItem>
              </SidebarSection>
            )}

            <AuthorizedSidebarSection
              sectionHeader={<SidebarHeading>Datasets</SidebarHeading>}
              permissions={{
                canAccessMedicaidDatasets: hasPolicies(['isAnyArtiaUser']),
                canAccessDrugUtilizations: hasPolicies(['isAnyArtiaUser']),
                canAccessNadac: hasPolicies(['isAnyArtiaUser']),
              }}
            >
              {({ permissions }) => (
                <>
                  {permissions.canAccessMedicaidDatasets && (
                    <SidebarItem href="/medicaid-datasets" current={isCurrentRoute('/medicaid-datasets')}>
                      <LiaCloudDownloadAltSolid size="1.25rem"/>
                      <SidebarLabel>Medicaid Datasets</SidebarLabel>
                    </SidebarItem>
                  )}
                  {permissions.canAccessDrugUtilizations && (
                    <SidebarItem href="/drug-utilizations" current={isCurrentRoute('/drug-utilizations')}>
                      <LiaFileMedicalAltSolid size="1.25rem"/>
                      <SidebarLabel>Drug Utilizations</SidebarLabel>
                    </SidebarItem>
                  )}
                  {permissions.canAccessNadac && (
                    <SidebarItem href="/nadac" current={isCurrentRoute('/nadac')}>
                      <LiaDatabaseSolid size="1.25rem"/>
                      <SidebarLabel>NADAC</SidebarLabel>
                    </SidebarItem>
                  )}
                </>
              )}
            </AuthorizedSidebarSection>
            {isClientUser && (
              <>
                <SidebarSpacer/>
                <SidebarSection>
                  <SidebarItem href="mailto:capsule@artiasolutions.com">
                    <LiaQuestionCircleSolid size="1.25rem"/>
                    <SidebarLabel>Support</SidebarLabel>
                  </SidebarItem>
                </SidebarSection>
              </>
            )}
          </SidebarBody>
          <SidebarFooter className="max-lg:hidden">
            <Dropdown>
              <DropdownButton as={SidebarItem}>
                <span className="flex min-w-0 items-center gap-3">
                  <Avatar src={user?.profile.picture} className="size-10" square alt=""/>
                  <span className="min-w-0">
                    <span className="block truncate text-base/5 font-medium text-zinc-950 dark:text-white">{user?.name}</span>
                    <span className="block truncate text-sm/5 font-normal text-zinc-500 dark:text-zinc-300">
                      {user?.profile.email}
                    </span>
                  </span>
                </span>
                <LiaAngleUpSolid/>
              </DropdownButton>
              <DropdownMenu className="min-w-64" anchor="top start">
                {isUserSettingsAvailable && (
                  <DropdownItem href="/user-settings">
                    <LiaUserCogSolid size="1.25rem"/>
                    <DropdownLabel>User Settings</DropdownLabel>
                  </DropdownItem>
                )}
                <DropdownItem onClick={handleSignout}>
                  <LiaSignOutAltSolid size="1.25rem"/>
                  <DropdownLabel>Sign out</DropdownLabel>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </SidebarFooter>
        </Sidebar>
      }
      navbar={
        <Navbar>
          <NavbarSpacer/>
          <NavbarSection>
            <NavbarItem href="mailto:capsule@artiasolutions.com" aria-label="Support">
              <LiaQuestionCircle/>
            </NavbarItem>

            <Dropdown>
              <DropdownButton as={NavbarItem}>
                <Avatar src={user?.profile.picture} square/>
              </DropdownButton>
              <DropdownMenu className="min-w-64" anchor="bottom end">
                {isUserSettingsAvailable && (
                  <DropdownItem href="/user-settings">
                    <LiaUserCogSolid size="1.25rem"/>
                    <DropdownLabel>User Settings</DropdownLabel>
                  </DropdownItem>
                )}
                <DropdownItem onClick={handleSignout}>
                  <LiaSignOutAltSolid size="1.25rem"/>
                  <DropdownLabel>Sign out</DropdownLabel>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </NavbarSection>
        </Navbar>
      }
    >
      <AnimatedPage animation="fadeIn" kind="full" flex fill>
        <div className="flex size-full min-h-0 flex-1 flex-col self-start">
          {hasHeaderDetails(headerDetails) && (
            <div className="mb-4">
              {headerDetails.breadcrumbs && <Breadcrumbs breadcrumbs={headerDetails.breadcrumbs}/>}
              <PageTitleRow title={headerDetails.pageTitle}>
                {headerDetails.actions}
              </PageTitleRow>
            </div>
          )}
          {children}
        </div>
        <div className="pt-12 text-center text-gray-500">
          &copy; {currentYear} by Artia Solutions, LLC
          <span className="mx-2">&middot;</span>
          <Link href="/terms-of-use" target="_blank" rel="noopener noreferrer" className="text-sm underline hover:text-gray-700">
            Terms of Use
          </Link>
        </div>
      </AnimatedPage>
      <NewClientOnboardingDialog user={user}/>
      {isAiEnabled && <AskArtiaPanel open={showAskArtia} onClose={() => setShowAskArtia(false)} />}
    </SidebarLayout>
  );
};

export { DashboardLayout };
